/**
 * Fluxo de emparelhamento, reaproveitado nos três modos (modal, painel lateral,
 * tela cheia) e nos dois caminhos de autenticação:
 *
 * - **QR Code** (Open WA, Evolution) — o código chega pelo WebSocket do gateway
 *   e é renovado automaticamente; o contador espelha a expiração real.
 * - **Credenciais da Meta** (API oficial) — sem QR: WABA ID, ID do número e
 *   token permanente, validados na Graph API antes de criar a conexão.
 */
import { Icon } from "@iconify/react";
import { useEffect, useState, type ReactNode } from "react";

import { Botao, Campo, Segmented, Tag } from "../components/Sga";
import { useSetores } from "../screens/MenuDeSetores";
import { api } from "../lib/api";
import { telefone } from "../lib/format";
import type { Canal, Conexao, PerfilAntiban } from "../lib/types";

export type ModoApresentacao = "modal" | "lateral" | "cheia";
type Passo = "autenticar" | "nomear";

/**
 * Marca do provedor, para a tela de sessão ativa.
 *
 * Aparece só depois do pareamento concluído, e existe por um motivo prático:
 * quem opera tem várias conexões de WhatsApp abertas ao mesmo tempo, umas por
 * Open WA e outras por Evolution. O rótulo do canal está no cadastro, longe
 * dali — a marca diz de relance por qual provedor aquela sessão subiu.
 *
 * Canal sem arquivo cadastrado simplesmente não mostra nada: a confirmação já
 * se explica sem a marca, e um ícone quebrado seria pior que a ausência dela.
 */
const LOGO_DO_CANAL: Partial<Record<Canal, string>> = {
  openwa: "/logos/openwa.svg",
};

const CANAIS: { valor: Canal; rotulo: string }[] = [
  { valor: "openwa", rotulo: "Open WA" },
  { valor: "evolution", rotulo: "Evolution" },
  // Dois Telegram, e a distincao importa na hora de escolher: um e o numero
  // da empresa (o cliente manda mensagem para o contato de sempre), o outro e
  // um @bot que o cliente precisa procurar e iniciar.
  { valor: "telegram_conta", rotulo: "Telegram" },
  { valor: "telegram", rotulo: "Telegram (bot)" },
  { valor: "cloud", rotulo: "API oficial" },
  { valor: "email", rotulo: "E-mail" },
  { valor: "form", rotulo: "Formulário" },
];

/**
 * Presets de provedor.
 *
 * Digitar `smtp.office365.com` de cabeça é onde o cadastro erra: host trocado
 * com IMAP, porta 465 onde o provedor só aceita 587. `dominio` deriva o host
 * do próprio endereço — é o caso do Carbonio e de qualquer servidor da casa,
 * onde `mail.<domínio>` é a convenção.
 */
const PRESETS_EMAIL: {
  rotulo: string;
  icone: string;
  smtp: string | "dominio";
  imap: string | "dominio";
  smtpPorta: string;
  imapPorta: string;
}[] = [
  {
    rotulo: "Carbonio / servidor próprio",
    icone: "mdi:server-outline",
    smtp: "dominio",
    imap: "dominio",
    smtpPorta: "587",
    imapPorta: "993",
  },
  {
    rotulo: "Google Workspace",
    icone: "mdi:google",
    smtp: "smtp.gmail.com",
    imap: "imap.gmail.com",
    smtpPorta: "587",
    imapPorta: "993",
  },
  {
    rotulo: "Microsoft 365",
    icone: "mdi:microsoft-outlook",
    smtp: "smtp.office365.com",
    imap: "outlook.office365.com",
    smtpPorta: "587",
    imapPorta: "993",
  },
];


interface Props {
  modo: ModoApresentacao;
  /** Conexão existente quando é reemparelhamento; `null` em conexão nova. */
  conexao: Conexao | null;
  perfis: PerfilAntiban[];
  callbackCloud: string;
  aoFechar: () => void;
  aoConcluir: (nome: string) => void;
  /** QR empurrado pelo WebSocket para esta conexão. */
  qrDoWs: { conexao_id: string; qr: string; expira_em_s: number } | null;
  /** Sessão autenticou (evento do WebSocket) — avança o passo sozinho. */
  autenticouViaWs: string | null;
}

export function QrPairing({
  modo,
  conexao,
  perfis,
  callbackCloud,
  aoFechar,
  aoConcluir,
  qrDoWs,
  autenticouViaWs,
}: Props) {
  const reemparelhando = conexao !== null;

  const [passo, setPasso] = useState<Passo>("autenticar");
  const setoresDoTenant = useSetores();
  const [canal, setCanal] = useState<Canal>(conexao?.canal ?? "openwa");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** O provedor aceitou o pedido e está gerando o QR de forma assíncrona. */
  const [aguardandoProvedor, setAguardandoProvedor] = useState(false);

  // Sessão em andamento
  const [conexaoId, setConexaoId] = useState<string | null>(
    conexao?.id ?? null,
  );
  const [qr, setQr] = useState<string | null>(null);
  const [segundos, setSegundos] = useState(45);
  const [identidade, setIdentidade] = useState<{
    numero: string | null;
    device: string | null;
  } | null>(null);

  // Passo 2
  const [nome, setNome] = useState(conexao?.nome ?? "");
  const [setor, setSetor] = useState(conexao?.setor ?? "");
  const [perfilKind, setPerfilKind] = useState<PerfilAntiban["kind"]>("padrao");
  const [desk, setDesk] = useState(true);
  const [gestor, setGestor] = useState(false);
  const [petro, setPetro] = useState(false);

  // Credenciais da Meta
  const [wabaId, setWabaId] = useState(conexao?.waba_id ?? "");
  const [phoneNumberId, setPhoneNumberId] = useState(
    conexao?.phone_number_id ?? "",
  );
  const [token, setToken] = useState("");
  const [verifyToken, setVerifyToken] = useState("");

  // Credenciais da caixa de e-mail. `senha` nunca volta da API: o gateway a
  // guarda cifrada e só o container de e-mail a recebe, uma vez, ao abrir a
  // sessão.
  const [emailEndereco, setEmailEndereco] = useState(conexao?.numero ?? "");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPorta, setSmtpPorta] = useState("587");
  const [imapHost, setImapHost] = useState("");
  const [imapPorta, setImapPorta] = useState("993");
  const [emailUsuario, setEmailUsuario] = useState("");
  const [emailSenha, setEmailSenha] = useState("");
  const [emailPasta, setEmailPasta] = useState("INBOX");

  const usaQr = canal === "openwa" || canal === "evolution" || canal === "telegram_conta";
  const ehEmail = canal === "email";
  const ehForm = canal === "form";

  /** Link da fila, gerado assim que a conexão de formulário nasce. */
  const [linkFila, setLinkFila] = useState<string | null>(null);
  const [copiouLink, setCopiouLink] = useState(false);

  const copiarLinkFila = async () => {
    if (!linkFila) return;
    try {
      await navigator.clipboard.writeText(linkFila);
      setCopiouLink(true);
      window.setTimeout(() => setCopiouLink(false), 1800);
    } catch {
      window.prompt("Copie o link:", linkFila);
    }
  };

  /** Campos que o gateway exige para abrir a sessão de e-mail. */
  const emailCompleto =
    emailEndereco.includes("@") &&
    smtpHost.trim() !== "" &&
    imapHost.trim() !== "" &&
    emailSenha !== "";

  /** Preenche host e porta a partir do preset, sem tocar em endereço e senha. */
  const aplicarPreset = (preset: (typeof PRESETS_EMAIL)[number]) => {
    const dominio = emailEndereco.split("@")[1]?.trim();
    const resolver = (valor: string) =>
      valor === "dominio" ? (dominio ? `mail.${dominio}` : "") : valor;

    setSmtpHost(resolver(preset.smtp));
    setImapHost(resolver(preset.imap));
    setSmtpPorta(preset.smtpPorta);
    setImapPorta(preset.imapPorta);
  };

  const camposEmail = {
    email_endereco: emailEndereco.trim(),
    email_smtp_host: smtpHost.trim(),
    email_smtp_porta: Number(smtpPorta) || 587,
    email_imap_host: imapHost.trim(),
    email_imap_porta: Number(imapPorta) || 993,
    email_usuario: emailUsuario.trim() || undefined,
    email_senha: emailSenha,
    email_pasta: emailPasta.trim() || "INBOX",
  };

  // QR vindo do WebSocket: substitui o que estiver na tela e reinicia a contagem.
  useEffect(() => {
    if (!qrDoWs) return;
    if (conexaoId && qrDoWs.conexao_id !== conexaoId) return;
    setQr(qrDoWs.qr);
    setSegundos(qrDoWs.expira_em_s);
    setAguardandoProvedor(false);
  }, [qrDoWs, conexaoId]);

  // Sessão autenticou no aparelho: o passo avança sem o operador clicar em nada.
  useEffect(() => {
    if (
      autenticouViaWs &&
      autenticouViaWs === conexaoId &&
      passo === "autenticar"
    ) {
      setPasso("nomear");
    }
  }, [autenticouViaWs, conexaoId, passo]);

  // Contador do QR. Ao zerar, pede um código novo — é o que o gateway faz de
  // qualquer forma, e a tela não pode ficar com QR morto.
  useEffect(() => {
    if (!qr || passo !== "autenticar") return;
    if (segundos <= 0) {
      gerarNovoCodigo();
      return;
    }
    const t = window.setTimeout(() => setSegundos((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr, segundos, passo]);

  /** Cria a conexão (quando nova) e abre a sessão no container do canal. */
  const iniciar = async () => {
    setErro(null);
    setOcupado(true);
    try {
      let id = conexaoId;

      if (!id) {
        // Conexão nova: nasce com nome provisório e é renomeada no passo 2.
        const criada = await api.post<{ conexao: Conexao }>("/conexoes", {
          nome:
            nome.trim() || `Conexão ${new Date().toLocaleDateString("pt-BR")}`,
          canal,
          setor: setor || null,
          perfil_antiban: usaQr ? perfilKind : "nao_aplica",
          integra_desk: desk,
          integra_gestor: gestor,
          integra_petro: petro,
          ...(canal === "cloud"
            ? {
                waba_id: wabaId,
                phone_number_id: phoneNumberId,
                token_permanente: token,
              }
            : {}),
          ...(ehEmail ? camposEmail : {}),
        });
        id = criada.conexao.id;
        setConexaoId(id);
      }

      const sessao = await api.post<{
        estado: string;
        qr: string | null;
        expira_em_s: number | null;
        sem_qr: boolean;
        conexao?: Conexao;
      }>(`/conexoes/${id}/sessao`, {
        ...(canal === "cloud"
          ? {
              waba_id: wabaId,
              phone_number_id: phoneNumberId,
              token_permanente: token || undefined,
            }
          : {}),
      });

      if (sessao.qr) {
        setQr(sessao.qr);
        setSegundos(sessao.expira_em_s ?? 45);
        setAguardandoProvedor(false);
      } else if (usaQr) {
        // Evolution não devolve o código na resposta: ele chega pelo evento
        // `qrcode.updated` do webhook e entra por `qrDoWs`.
        setAguardandoProvedor(true);
      }

      // Formulário: a conexão nasce junto com o link da fila. Fazer o operador
      // salvar, sair e voltar no cadastro só para pegar o link seria um passo a
      // mais no momento em que ele está justamente criando a porta de entrada.
      if (ehForm) {
        const d = await api.post<{ link: { url: string } }>("/links-fila", {
          conexao_id: id,
          rotulo: "Suporte",
        });
        setLinkFila(d.link.url);
        setIdentidade({
          numero: sessao.conexao?.numero ?? null,
          device: sessao.conexao?.device_label ?? null,
        });
        return;
      }

      if (sessao.estado === "autenticada") {
        setIdentidade({
          numero: sessao.conexao?.numero ?? null,
          device: sessao.conexao?.device_label ?? null,
        });
        setPasso("nomear");
      }
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const gerarNovoCodigo = async () => {
    if (!conexaoId) {
      await iniciar();
      return;
    }
    setErro(null);
    try {
      const novo = await api.get<{
        qr?: string;
        expira_em_s?: number;
        erro?: { codigo: string };
      }>(`/conexoes/${conexaoId}/qr`);
      if (novo.qr) {
        setQr(novo.qr);
        setSegundos(novo.expira_em_s ?? 45);
        setAguardandoProvedor(false);
      } else {
        // 202: o provedor aceitou e vai entregar o código pelo webhook.
        setAguardandoProvedor(true);
      }
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  /** Validar credenciais da Meta antes de gravar qualquer coisa. */
  const validarCredenciais = async () => {
    setErro(null);
    setOcupado(true);
    try {
      const r = await api.post<{
        valido: boolean;
        numero: string | null;
        nome_verificado: string | null;
        qualidade: string | null;
      }>("/conexoes/validar-oficial", {
        waba_id: wabaId.trim(),
        phone_number_id: phoneNumberId.trim(),
        token_permanente: token,
      });
      setIdentidade({
        numero: r.numero,
        device: `WABA ${wabaId} · qualidade ${r.qualidade ?? "desconhecida"}`,
      });
      if (r.nome_verificado && !nome) setNome(r.nome_verificado);
      setPasso("nomear");
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  /** Passo 2: nomeia e (se ainda não existe) cria a conexão de fato. */
  const salvar = async () => {
    setErro(null);
    setOcupado(true);
    try {
      if (conexaoId) {
        await api.patch(`/conexoes/${conexaoId}`, {
          nome: nome.trim(),
          setor: setor || null,
          perfil_antiban: usaQr ? perfilKind : "nao_aplica",
          integra_desk: desk,
          integra_gestor: gestor,
          integra_petro: petro,
        });
      } else {
        const criada = await api.post<{ conexao: Conexao }>("/conexoes", {
          nome: nome.trim(),
          canal,
          setor: setor || null,
          perfil_antiban: usaQr ? perfilKind : "nao_aplica",
          integra_desk: desk,
          integra_gestor: gestor,
          integra_petro: petro,
          waba_id: wabaId || undefined,
          phone_number_id: phoneNumberId || undefined,
          token_permanente: token || undefined,
        });
        setConexaoId(criada.conexao.id);
      }
      aoConcluir(nome.trim());
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const compacto = modo === "lateral";
  const espacoso = modo === "cheia";
  const tamanhoQr = compacto ? 220 : espacoso ? 300 : 260;

  const corpo = (
    <div
      className="sga-modal__body"
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <Stepper passo={passo} canal={canal} />

      {erro && (
        <div
          className="sga-aviso-warn"
          style={{
            background: "rgba(255,77,79,.12)",
            color: "var(--sga-error)",
          }}
        >
          <Icon icon="mdi:alert-circle-outline" width={17} />
          {erro}
        </div>
      )}

      {passo === "autenticar" ? (
        <>
          {/* O canal escolhido decide quais campos existem abaixo. Ficava
              espremido na coluna da direita, com cinco opções quebrando o
              rótulo em duas linhas; em largura total cada uma cabe numa linha
              e a decisão vem antes do formulário que ela governa. */}
          {!reemparelhando && (
            <Campo rotulo="Canal">
              <Segmented
                valor={canal}
                onChange={setCanal}
                opcoes={CANAIS}
                largo
              />
            </Campo>
          )}
          <div
            style={{
              display: "grid",
              // No e-mail a coluna do formulário pesa mais que a das instruções:
              // são dois pares host+porta que precisam caber lado a lado.
              gridTemplateColumns: compacto
                ? "1fr"
                : ehEmail
                  ? "1.45fr 1fr"
                  : "1fr 1fr",
              gap: 22,
              alignItems: "start",
            }}
          >
            {usaQr ? (
              <div className="sga-qr-painel">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                  }}
                >
                  <span
                    className="sga-dot sga-dot--pulsa"
                    style={{ background: "var(--sga-warning)" }}
                  />
                  {qr
                    ? "Aguardando leitura"
                    : aguardandoProvedor
                      ? "Gerando no provedor"
                      : "Pronto para gerar"}
                  <span style={{ flex: 1 }} />
                  <span className="sga-qr-contador">
                    expira em 00:
                    {String(Math.max(0, segundos)).padStart(2, "0")}
                  </span>
                </div>

                {qr ? (
                  <img
                    src={qr}
                    alt="QR Code de emparelhamento"
                    width={tamanhoQr}
                    height={tamanhoQr}
                  />
                ) : (
                  <div
                    style={{
                      width: tamanhoQr,
                      height: tamanhoQr,
                      display: "grid",
                      placeItems: "center",
                      background: "var(--sga-gray-05)",
                      borderRadius: 10,
                      color: "var(--sga-fg-3)",
                      fontSize: 12.5,
                      textAlign: "center",
                      padding: 20,
                    }}
                  >
                    {ocupado
                      ? "Gerando código…"
                      : aguardandoProvedor
                        ? "Aguardando o provedor gerar o código. Ele aparece aqui sozinho, pelo WebSocket."
                        : "Clique em “Gerar código” para iniciar o emparelhamento."}
                  </div>
                )}

                <div className="hint" style={{ textAlign: "center" }}>
                  Renovado automaticamente pelo WebSocket do gateway
                </div>
                <Botao
                  icone="mdi:refresh"
                  onClick={gerarNovoCodigo}
                  disabled={ocupado}
                >
                  {qr ? "Gerar novo código" : "Gerar código"}
                </Botao>
              </div>
            ) : canal === "cloud" ? (
              <div className="sga-qr-painel" style={{ alignItems: "stretch" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong style={{ fontSize: 14, fontWeight: 500 }}>
                    Credenciais da Meta
                  </strong>
                  <Tag kind="mut">sem QR Code</Tag>
                </div>
                <Campo rotulo="WABA ID" obrigatorio>
                  <input
                    className="input mono"
                    value={wabaId}
                    onChange={(e) => setWabaId(e.target.value)}
                  />
                </Campo>
                <Campo rotulo="ID do número de telefone" obrigatorio>
                  <input
                    className="input mono"
                    value={phoneNumberId}
                    onChange={(e) => setPhoneNumberId(e.target.value)}
                  />
                </Campo>
                <Campo
                  rotulo="Token permanente"
                  obrigatorio
                  hint="Guardado cifrado no gateway. Nunca é devolvido pela API."
                >
                  <input
                    className="input mono"
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoComplete="off"
                  />
                </Campo>
                <Campo
                  rotulo="Verify token do webhook"
                  hint="Opcional: o gateway gera um se ficar vazio."
                >
                  <input
                    className="input mono"
                    value={verifyToken}
                    onChange={(e) => setVerifyToken(e.target.value)}
                  />
                </Campo>
                <div>
                  <div className="hint" style={{ marginBottom: 4 }}>
                    URL de callback para cadastrar na Meta
                  </div>
                  <div className="sga-bloco-cinza">{callbackCloud}</div>
                </div>
              </div>
            ) : ehForm ? (
            <div className="sga-qr-painel" style={{ alignItems: "stretch" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 14, fontWeight: 500 }}>Link de suporte</strong>
                <Tag kind="mut">sem QR Code</Tag>
              </div>

              {linkFila ? (
                <>
                  <div className="aviso-ok-inline">
                    <Icon icon="mdi:check-circle-outline" width={17} />
                    Formulário publicado. Este é o link para os seus clientes.
                  </div>
                  <div className="cad-copia">
                    <code>{linkFila}</code>
                    <button type="button" onClick={copiarLinkFila}>
                      <Icon
                        icon={copiouLink ? "mdi:check-bold" : "mdi:content-copy"}
                        width={14}
                      />
                      {copiouLink ? "Copiado" : "Copiar"}
                    </button>
                  </div>
                  <p className="hint" style={{ marginTop: 0 }}>
                    Publique no site, na assinatura de e-mail e na nota fiscal. Ele não muda —
                    guarde-o. Para trocar depois, use “Rotacionar” no cadastro da conexão.
                  </p>
                </>
              ) : (
                <p className="hint" style={{ marginTop: 0 }}>
                  Ao criar, o sistema gera <strong>um link só</strong> para esta fila. Qualquer
                  cliente pode usá-lo: ele se identifica no primeiro acesso — nome do posto, CNPJ,
                  celular — e o navegador dele lembra depois.
                </p>
              )}
            </div>
          ) : ehEmail ? (
              <div className="sga-qr-painel" style={{ alignItems: "stretch" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong style={{ fontSize: 14, fontWeight: 500 }}>
                    Caixa de e-mail
                  </strong>
                  <Tag kind="mut">sem QR Code</Tag>
                </div>
                <Campo
                  rotulo="Endereço"
                  obrigatorio
                  hint="É por ele que o cliente recebe e responde."
                >
                  <input
                    className="input mono"
                    type="email"
                    placeholder="atendimento@gruposgapetro.com.br"
                    value={emailEndereco}
                    onChange={(e) => setEmailEndereco(e.target.value)}
                  />
                </Campo>

                <div style={{ display: "flex", gap: 12 }}>
                  <Campo rotulo="Servidor SMTP (envio)" obrigatorio flex={3}>
                    <input
                      className="input mono"
                      placeholder="smtp.gruposgapetro.com.br"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                    />
                  </Campo>
                  <Campo rotulo="Porta" flex={1} hint="587 STARTTLS · 465 SSL">
                    <input
                      className="input mono"
                      inputMode="numeric"
                      value={smtpPorta}
                      onChange={(e) => setSmtpPorta(e.target.value)}
                    />
                  </Campo>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <Campo
                    rotulo="Servidor IMAP (recebimento)"
                    obrigatorio
                    flex={3}
                  >
                    <input
                      className="input mono"
                      placeholder="imap.gruposgapetro.com.br"
                      value={imapHost}
                      onChange={(e) => setImapHost(e.target.value)}
                    />
                  </Campo>
                  <Campo rotulo="Porta" flex={1} hint="993 SSL · 143 STARTTLS">
                    <input
                      className="input mono"
                      inputMode="numeric"
                      value={imapPorta}
                      onChange={(e) => setImapPorta(e.target.value)}
                    />
                  </Campo>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <Campo
                    rotulo="Usuário"
                    flex={1}
                    hint="Vazio = o próprio endereço."
                  >
                    <input
                      className="input mono"
                      value={emailUsuario}
                      onChange={(e) => setEmailUsuario(e.target.value)}
                      autoComplete="off"
                    />
                  </Campo>
                  <Campo rotulo="Pasta monitorada" flex={1}>
                    <input
                      className="input mono"
                      value={emailPasta}
                      onChange={(e) => setEmailPasta(e.target.value)}
                    />
                  </Campo>
                </div>

                <Campo
                  rotulo="Senha"
                  obrigatorio
                  hint="Guardada cifrada no gateway. Nunca é devolvida pela API."
                >
                  <input
                    className="input mono"
                    type="password"
                    value={emailSenha}
                    onChange={(e) => setEmailSenha(e.target.value)}
                    autoComplete="off"
                  />
                </Campo>
              </div>
            ) : (
              <div className="sga-qr-painel" style={{ alignItems: "stretch" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <strong style={{ fontSize: 14, fontWeight: 500 }}>
                    Token do bot
                  </strong>
                  <Tag kind="mut">sem QR Code</Tag>
                </div>
                <Campo
                  rotulo="Token do BotFather"
                  obrigatorio
                  hint="O gateway registra o webhook do bot automaticamente."
                >
                  <input
                    className="input mono"
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoComplete="off"
                  />
                </Campo>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <strong style={{ fontSize: 13.5, fontWeight: 500 }}>
                  {canal === "cloud"
                    ? "Como habilitar"
                    : ehEmail
                      ? "Como conectar"
                      : ehForm
                        ? "Como funciona"
                        : "Como emparelhar"}
                </strong>
                <ol className="sga-passos-numerados">
                  {canal === "cloud" ? (
                    <>
                      <li>Crie o app do WhatsApp na Meta Business Suite.</li>
                      <li>
                        Gere um token permanente com{" "}
                        <code>whatsapp_business_messaging</code> e{" "}
                        <code>whatsapp_business_management</code>.
                      </li>
                      <li>
                        Cadastre a URL de callback acima no webhook do app.
                      </li>
                      <li>
                        Tenha os templates aprovados antes do primeiro disparo.
                      </li>
                    </>
                  ) : ehForm ? (
                  <>
                    <li>Dê um nome à fila — “Suporte N1”, “Financeiro”.</li>
                    <li>O sistema gera um link único para ela.</li>
                    <li>Publique esse link onde seus clientes o encontrem.</li>
                    <li>
                      Para link dedicado por CNPJ, use <strong>Clientes e links</strong>.
                    </li>
                  </>
                ) : ehEmail ? (
                    <>
                      <li>
                        Crie uma caixa dedicada ao atendimento no servidor de
                        e-mail.
                      </li>
                      <li>
                        Preencha SMTP e IMAP com os dados que o provedor
                        informa.
                      </li>
                      <li>
                        Publique SPF, DKIM e DMARC no DNS do domínio antes de
                        disparar.
                      </li>
                      <li>
                        O gateway entra em IMAP IDLE: a resposta do cliente
                        chega em segundos.
                      </li>
                    </>
                  ) : canal === "telegram" ? (
                    <>
                      <li>Fale com o @BotFather no Telegram.</li>
                      <li>Crie o bot e copie o token.</li>
                      <li>Cole o token no campo ao lado.</li>
                      <li>Adicione o bot ao grupo ou canal de destino.</li>
                    </>
                  ) : canal === "telegram_conta" ? (
                    <>
                      <li>Abra o Telegram no aparelho da conexão.</li>
                      <li>Vá em Configurações → Dispositivos.</li>
                      <li>Toque em “Vincular dispositivo”.</li>
                      <li>Aponte a câmera para o QR Code ao lado.</li>
                      <li>
                        O código expira a cada 30 segundos e se renova sozinho —
                        se ele trocar enquanto você aponta, é normal.
                      </li>
                    </>
                  ) : (
                    <>
                      <li>Abra o WhatsApp no aparelho da conexão.</li>
                      <li>Vá em Configurações → Aparelhos conectados.</li>
                      <li>Toque em “Conectar um aparelho”.</li>
                      <li>Aponte a câmera para o QR Code ao lado.</li>
                    </>
                  )}
                </ol>
              </div>

              {ehEmail && (
                <div className="sga-presets">
                  <div className="sga-presets__titulo">
                    Preencher servidores
                    <span className="hint" style={{ marginTop: 0 }}>
                      Digite o endereço primeiro nos servidores próprios
                    </span>
                  </div>
                  {PRESETS_EMAIL.map((preset) => (
                    <button
                      key={preset.rotulo}
                      type="button"
                      className="sga-presets__item"
                      onClick={() => aplicarPreset(preset)}
                    >
                      <Icon icon={preset.icone} width={17} />
                      <span style={{ flex: 1, textAlign: "left" }}>
                        {preset.rotulo}
                      </span>
                      <span className="mono" style={{ fontSize: 11.5 }}>
                        {preset.smtpPorta} · {preset.imapPorta}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div className="sga-aviso-warn">
                <Icon
                  icon={
                    canal === "cloud"
                      ? "mdi:check-decagram"
                      : ehEmail
                        ? "mdi:email-alert-outline"
                        : "mdi:shield-alert-outline"
                  }
                  width={17}
                />
                {canal === "cloud"
                  ? "Na API oficial a Meta cobra por conversa e a janela de atendimento é de 24 h — fora dela só template aprovado."
                  : ehEmail
                    ? "Domínio sem SPF, DKIM e DMARC cai em spam. O limite de envio por hora protege a reputação — não é anti-ban, é entregabilidade."
                    : "Número novo precisa de 7 dias de aquecimento: o Anti-Ban escalona o limite por hora nesse período."}
              </div>
            </div>
          </div>

          {/* Rodapé ancorado. Antes as ações ficavam no fim da coluna direita,
              que é bem mais curta que o formulário: os botões paravam no meio
              da altura do modal, com um vazio embaixo. Aqui eles fecham o
              diálogo, como em qualquer modal. */}
          <div className="sga-modal__acoes">
            {canal === "cloud" ? (
              <Botao
                variante="primary"
                icone="mdi:check-decagram"
                onClick={validarCredenciais}
                disabled={
                  ocupado || !wabaId.trim() || !phoneNumberId.trim() || !token
                }
              >
                {ocupado ? "Validando…" : "Validar credenciais"}
              </Botao>
            ) : (
              <Botao
                variante="primary"
                icone={
                  ehForm
                    ? linkFila
                      ? "mdi:arrow-right"
                      : "mdi:link-plus"
                    : ehEmail
                      ? "mdi:email-check-outline"
                      : "mdi:cellphone-check"
                }
                onClick={ehForm && linkFila ? () => setPasso("nomear") : iniciar}
                disabled={
                  ocupado ||
                  (canal === "telegram" && !token) ||
                  (ehEmail && !emailCompleto)
                }
              >
                {ocupado
                  ? "Aguarde…"
                  : usaQr
                    ? "Iniciar emparelhamento"
                    : ehForm
                      ? linkFila
                        ? "Continuar"
                        : "Criar e gerar link"
                      : ehEmail
                        ? "Conectar caixa"
                        : "Registrar bot"}
              </Botao>
            )}
            <Botao variante="warning" onClick={aoFechar}>
              Voltar
            </Botao>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="sga-confirmacao">
            <span className="sga-confirmacao__circulo">
              <Icon icon="mdi:check-bold" width={20} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: "var(--sga-fg-3)" }}>
                {ehForm
                  ? "Link de suporte"
                  : ehEmail
                    ? "Caixa conectada"
                    : "Número autenticado"}
              </div>
              {ehForm && linkFila ? (
                <>
                  {/* O que interessa aqui é o link da fila, não a URL interna
                      do formulário genérico: é este endereço que vai para o
                      site e para a nota fiscal. */}
                  <div className="cad-copia" style={{ marginTop: 2 }}>
                    <code>{linkFila}</code>
                    <button type="button" onClick={copiarLinkFila}>
                      <Icon
                        icon={copiouLink ? "mdi:check-bold" : "mdi:content-copy"}
                        width={14}
                      />
                      {copiouLink ? "Copiado" : "Copiar"}
                    </button>
                  </div>
                  <div
                    style={{ fontSize: 11.5, color: "var(--sga-fg-3)", marginTop: 4 }}
                  >
                    Guarde este link — ele não muda. Fica também no cadastro da conexão.
                  </div>
                </>
              ) : (
                <>
                  <div className="sga-confirmacao__numero">
                    {telefone(identidade?.numero ?? conexao?.numero ?? null)}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--sga-fg-3)" }}>
                    {identidade?.device ?? conexao?.device_label ?? "—"}
                  </div>
                </>
              )}
            </div>
            {!ehForm && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {LOGO_DO_CANAL[canal] && (
                  <img
                    src={LOGO_DO_CANAL[canal]}
                    alt=""
                    className="sga-confirmacao__marca"
                    /* Arquivo ausente ou corrompido some em silêncio. Sem isto,
                       o navegador desenha o ícone de imagem quebrada bem ao
                       lado de "Sessão ativa" — e nada passa mais a impressão
                       de que algo falhou do que isso, justamente na tela que
                       existe para dizer que deu certo. */
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <Tag>Sessão ativa</Tag>
              </div>
            )}
          </div>

          <Campo
            rotulo="Nome da conexão"
            obrigatorio
            hint="Aparece nas filas, nos logs e no SGA Desk."
          >
            <input
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={120}
              autoFocus
            />
          </Campo>

          <div className="line line-2">
            <Campo rotulo="Setor">
              <select
                className="select"
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
              >
                <option value="">Selecione…</option>
                {setoresDoTenant.map((x) => x.nome).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo rotulo="Perfil Anti-Ban">
              <select
                className="select"
                value={usaQr ? perfilKind : "nao_aplica"}
                onChange={(e) =>
                  setPerfilKind(e.target.value as PerfilAntiban["kind"])
                }
                disabled={!usaQr}
              >
                {perfis.map((p) => (
                  <option key={p.id} value={p.kind}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </Campo>
          </div>

          <div style={{ display: "flex", gap: 20, fontSize: 13.5 }}>
            {[
              ["SGA Desk", desk, setDesk] as const,
              ["SGA Gestor", gestor, setGestor] as const,
              ["SGA Petro", petro, setPetro] as const,
            ].map(([rotulo, valor, set]) => (
              <label
                key={rotulo}
                style={{ display: "flex", alignItems: "center", gap: 7 }}
              >
                <input
                  type="checkbox"
                  checked={valor}
                  onChange={(e) => set(e.target.checked)}
                  style={{ accentColor: "var(--sga-primary)" }}
                />
                {rotulo}
              </label>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <Botao
              variante="primary"
              icone="mdi:content-save-outline"
              onClick={salvar}
              disabled={ocupado || !nome.trim()}
            >
              {ocupado ? "Salvando…" : "Salvar"}
            </Botao>
            <Botao variante="warning" onClick={() => setPasso("autenticar")}>
              Voltar
            </Botao>
          </div>
          <span className="hint">
            A conexão entra na fila do tenant assim que salva.
          </span>
        </div>
      )}
    </div>
  );

  const cabecalho = (
    <div className="sga-modal__head">
      <Icon
        icon="mdi:qrcode-scan"
        width={22}
        style={{ color: "var(--sga-primary-text)" }}
      />
      <span className="sga-modal__titulo">
        {reemparelhando ? `Reemparelhar — ${conexao?.nome}` : "Nova conexão"}
      </span>
      <span style={{ flex: 1 }} />
      <button
        type="button"
        className="modal-close"
        onClick={aoFechar}
        aria-label="Fechar"
      >
        <Icon icon="mdi:close" width={18} />
      </button>
    </div>
  );

  return (
    <Apresentacao
      modo={modo}
      aoFechar={aoFechar}
      cabecalho={cabecalho}
      corpo={corpo}
      largo={ehEmail}
    />
  );
}

function Apresentacao({
  modo,
  aoFechar,
  cabecalho,
  corpo,
  largo,
}: {
  modo: ModoApresentacao;
  aoFechar: () => void;
  cabecalho: ReactNode;
  corpo: ReactNode;
  /** Canal com muitos campos (e-mail): o modal cresce em vez de espremer. */
  largo?: boolean;
}) {
  if (modo === "lateral") {
    return (
      <aside className="sga-painel-lateral" role="dialog" aria-modal="true">
        {cabecalho}
        {corpo}
      </aside>
    );
  }

  if (modo === "cheia") {
    return (
      <div className="sga-tela-cheia" role="dialog" aria-modal="true">
        <header className="sga-tela-cheia__header">
          <img
            src="/logos/sga-logo-branca.svg"
            alt="SGA"
            style={{ height: 24 }}
          />
          <strong style={{ fontSize: 15, fontWeight: 500 }}>
            Emparelhar nova conexão
          </strong>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={aoFechar}
          >
            Cancelar
          </button>
        </header>
        <div
          style={{
            maxWidth: largo ? 1180 : 980,
            margin: "0 auto",
            padding: 20,
          }}
        >
          {corpo}
        </div>
      </div>
    );
  }

  return (
    // Clicar no overlay fecha; clicar dentro do card não.
    <div className="sga-mask" onClick={aoFechar}>
      <div
        // O canal de e-mail tem oito campos: no modal padrão eles ficavam em
        // meia coluna, com as dicas quebrando em duas linhas.
        className={largo ? "sga-modal sga-modal--largo" : "sga-modal"}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {cabecalho}
        {corpo}
      </div>
    </div>
  );
}

function Stepper({ passo, canal }: { passo: Passo; canal: Canal }) {
  const passos = [
    {
      rotulo:
        canal === "cloud"
          ? "Validar credenciais"
          : canal === "form"
            ? "Gerar link"
            : "Autenticar",
      sub:
        canal === "cloud"
          ? "Meta Cloud API"
          : canal === "telegram"
            ? "Token do bot"
            : canal === "email"
              ? "SMTP e IMAP"
              : canal === "form"
                ? "Link da fila"
                : "Leitura do QR Code",
    },
    { rotulo: "Nomear", sub: "Setor e integrações" },
    { rotulo: "Na fila", sub: "Pronto para enviar" },
  ];
  const indiceAtual = passo === "autenticar" ? 0 : 1;

  return (
    <div className="sga-stepper">
      {passos.map((p, i) => (
        <div
          key={p.rotulo}
          className={`sga-stepper__passo${
            i === indiceAtual
              ? " sga-stepper__passo--ativo"
              : i < indiceAtual
                ? " sga-stepper__passo--feito"
                : ""
          }`}
        >
          <span className="sga-stepper__bolha">
            {i < indiceAtual ? (
              <Icon icon="mdi:check-bold" width={13} />
            ) : (
              i + 1
            )}
          </span>
          <span>
            <div className="sga-stepper__rotulo">{p.rotulo}</div>
            <div className="sga-stepper__sub">{p.sub}</div>
          </span>
        </div>
      ))}
    </div>
  );
}
